import { Box, Button, Flex, Heading, Image, Text } from '@everlywell/ui-kit';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React from 'react';
import { IGatsbyImage } from 'utils/types';

// Types ----------

export interface OptionProps {
  title: string;
  image?: IGatsbyImage;
  description?: string;
  isSelected: boolean;
  isIneligible: boolean;
  // isExclusive is used to determine if the user has selected an option that should clear all other selections
  isExclusive: boolean;
  handleOptionSelection: () => void;
}

// Option ------

const Option: React.FC<OptionProps> = (props) => {
  const { isSelected, handleOptionSelection, image, title, description } =
    props;

  // onKeyDown handles keyboard events for activating the option with the Enter or Space key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOptionSelection();
    }
  };

  return (
    <>
      {description ? (
        // Description, box style
        <Flex
          // role, tabIndex, and onKeyDown are required for keyboard accessibility
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          flexDirection="column"
          border="4px solid"
          borderColor={isSelected ? 'eggplant.lighter' : 'transparent'}
          borderRadius="12px"
          backgroundColor="tints.white"
          data-testid={
            isSelected
              ? 'quiz-question-option-selected'
              : 'quiz-question-option'
          }
          // Reducing the padding to account for the "selected" border of 4px
          // important is the only way to override the default padding
          padding="4"
          _last={{
            marginRight: 0,
          }}
          _hover={{
            cursor: ['default', null, 'pointer'],
          }}
          onClick={() => handleOptionSelection()}
          position="relative"
          height="100%"
        >
          {image && (
            <Box position="absolute" top="0" left="0">
              <Image
                as={Img}
                image={image.gatsbyImageData}
                alt={title}
                objectFit="fill"
                width={['40px', null, '45px']}
                height={['40px', null, '45px']}
                display="block"
              />
            </Box>
          )}
          <Flex flexDirection="column">
            <Text size="xl" as="span" paddingLeft={image ? '32px' : '0'}>
              {title}
            </Text>
            {description && (
              <Text size="md" color="tints.darkGrey" marginTop="1">
                {description}
              </Text>
            )}
          </Flex>
        </Flex>
      ) : (
        // No description, button style
        <Button
          border="4px solid"
          borderColor={isSelected ? 'eggplant.lighter' : 'transparent'}
          backgroundColor="tints.white"
          colorScheme="whiteCS"
          display="flex"
          data-testid={
            isSelected
              ? 'quiz-question-option-selected'
              : 'quiz-question-option'
          }
          marginBottom="3"
          marginRight={['10px', null, '4']}
          size="md"
          variant="primary"
          // Reducing the padding to account for the "selected" border of 4px
          // important is the only way to override the default padding
          padding="8px 12px !important"
          _last={{
            marginRight: 0,
          }}
          _hover={{
            cursor: ['default', null, 'pointer'],
          }}
          onClick={() => handleOptionSelection()}
          position="relative"
        >
          {image && (
            <Box position="absolute" top="0" left="0">
              <Image
                as={Img}
                image={image.gatsbyImageData}
                alt={title}
                objectFit="fill"
                width={['40px', null, '45px']}
                height={['40px', null, '45px']}
                display="block"
              />
            </Box>
          )}
          <Heading size="sm" as="span" paddingLeft={image ? '32px' : '0'}>
            {title}
          </Heading>
        </Button>
      )}
    </>
  );
};

// Export ---------

export default Option;
